import { v4 as uuidv4 } from "uuid";
import { addDays, endOfDay } from "date-fns";

import { API, graphqlOperation } from "aws-amplify";
import { getAccount } from "../../../graphql/account/account-queries";
import { getInvoiceCounter } from "../../../graphql/reloadyard/custom-queries";
import {
  performUpdateInvoiceMini,
  sanitizeInvoiceLambda,
} from "../../../graphql/invoice/invoiceCRUD";
import { sanitizeOutboundLambda } from "../../../graphql/outbound/outboundCRUD";
import { sanitizeInboundLambda } from "../../../graphql/inbound/inboundCRUD";

import {
  ExternalStatus,
  InventoryStatus,
  InvoiceStatus,
  MAX_INVOICE_LINE_ITEMS,
  NoteType,
  ResourceType,
  SORTKEY_BUFFER,
  Unit,
  UnitOptions,
  UnitToColumn,
} from "../../../utils/constants";

import { logError, logHistory } from "../../../utils/appUtils";
import { convertUnits, getConversionValue } from "../../../utils/loadTypeUtils";
import { formatCurrency, trimDecimal } from "../../../utils/utils";
import { fetchServices, getUnitPrice } from "../services/ServiceUtils";
import { getBillingAccountId } from "../accounts/AccountUtils";
import {
  applyChargeConstraints,
  buildShipmentLevelStorageBillableItem,
  calculateBillableDays,
  calculateStorageFees,
  calculateStorageFreeDays,
  doesRuleApply,
  fetchChargeRules,
  fetchInboundBillingRules,
  fetchOutboundBillingRules,
  findApplicableRule,
  getLeasedZoneMap,
  isLeasedZoneId,
  sortChargeRules,
  trimLeasedZoneInventories,
} from "../accounts/chargeRules/ChargeRuleUtils";
import {
  BillingAutomation,
  ChargeEvent,
  FeeAccrual,
  StorageFrequency,
} from "../accounts/chargeRules/chargeRuleConstants";

export const fetchAccountMap = async (event, shipmentMap) => {
  const accountMap = {};
  const uniqueAccountIds = new Set();

  // Collect unique account IDs
  Object.values(shipmentMap).forEach((shipment) => {
    const billingAccountId = getBillingAccountId(shipment);
    if (billingAccountId) {
      uniqueAccountIds.add(billingAccountId);
    }
  });

  if (uniqueAccountIds.size === 0) return accountMap;
  const accountIds = [...uniqueAccountIds];

  // Fetch accounts and services in parallel
  const accountPromises = accountIds.map((id) =>
    API.graphql(graphqlOperation(getAccount, { id })),
  );
  const servicePromises = accountIds.map((id) => fetchServices(id, true));
  const rulePromises = accountIds.map((id) =>
    event === ChargeEvent.INBOUND
      ? fetchInboundBillingRules(id)
      : event === ChargeEvent.OUTBOUND
        ? fetchOutboundBillingRules(id)
        : fetchChargeRules(id, event, true),
  );

  const [accountResults, serviceResults, rulesResults] = await Promise.all([
    Promise.all(accountPromises),
    Promise.all(servicePromises),
    Promise.all(rulePromises),
  ]);

  // Merge accounts with their services
  accountResults.forEach((res, index) => {
    const account = res.data.getAccount;

    // Add services to the account object
    account.services = serviceResults[index];
    // Attach all charge rules grouped by event;
    // execution (AUTO/MANUAL/CUSTOM) is handled at the point of use
    account.chargeRules = rulesResults[index] || [];

    accountMap[account.id] = account;
  });

  return accountMap;
};

export const fetchReloadYard = async () => {
  const result = await API.graphql(
    graphqlOperation(getInvoiceCounter, {
      id: window.appUser?.reloadYardId,
    }),
  );
  return result.data.getReloadYard;
};

export const createInvoiceBase = (
  account,
  inboundShipment,
  outboundShipment,
) => {
  const terms = getPaymentTerms(account.paymentTermId);
  const shipment = inboundShipment || outboundShipment;

  return {
    active: "true",
    groups: account.groups,
    reloadYardId: account.reloadYardId,
    accountId: account.id,
    accountName: account.businessName,
    account: account,
    poNumber: shipment?.poNumber || "",
    inboundShipment: inboundShipment,
    inboundShipmentId: inboundShipment?.id,
    outboundShipment: outboundShipment,
    outboundShipmentId: outboundShipment?.id,
    paymentTermId: terms?.id || "",
    paymentTerms: terms?.name || "",
    dueDate: getDueDate(terms),
    contact:
      account.billingContacts?.length > 0
        ? account.billingContacts[0]
        : undefined,
  };
};

const isShipmentUom = (u) => u === Unit.CAR || u === Unit.TL;

// ON_RELEASE and ON_PULL both bill a shipment's stay from placement;
// they differ only in the event that ends it.
const isShipmentStorageRule = (rule) =>
  rule?.event === ChargeEvent.STORAGE &&
  (rule?.frequency === StorageFrequency.ON_RELEASE ||
    rule?.frequency === StorageFrequency.ON_PULL);

const isOutOfStockStorageRule = (rule) =>
  rule?.event === ChargeEvent.STORAGE &&
  rule?.frequency === StorageFrequency.ON_DEPLETION;

const getUniqueOutboundInventories = (outbound) => [
  ...new Map(
    (outbound?.shipmentLineItems?.items || [])
      .map((shipmentLineItem) => shipmentLineItem?.inventory)
      .filter((inventory) => inventory?.id)
      .map((inventory) => [inventory.id, inventory]),
  ).values(),
];

const shouldSkipEventDrivenStorageForLeasedZones = ({
  record,
  inventories = [],
  accountId,
  leasedZoneMap = getLeasedZoneMap(accountId),
}) => {
  if (!accountId || !leasedZoneMap.size) return false;

  if (inventories.length) {
    return (
      trimLeasedZoneInventories(inventories, accountId, leasedZoneMap)
        .length === 0
    );
  }

  return isLeasedZoneId(record?.zoneId, accountId, leasedZoneMap);
};

// Used by ON_RELEASE and ON_PULL storage billing for calculating
// autoCharges and during invoiceing.
// ON_RELEASE ends at "Time Released" (outbound stores it as departureDateTime).
// ON_PULL ends at "Time Pulled" (collectedDateTime); ON_PULL rules are rail
// only, and invoicing waits for the pull (see hasOnPullStorageRule).
// Missing end dates mean the shipment has not reached its billing event yet,
// so callers skip billing.
const getShipmentStorageWindow = (shipment, direction, frequency) => {
  const startDate =
    direction === "outbound"
      ? shipment?.placementDateTime
      : shipment?.placedDateTime;
  const releaseDate =
    direction === "outbound"
      ? shipment?.departureDateTime
      : shipment?.releaseDateTime;
  const endDate =
    frequency === StorageFrequency.ON_PULL
      ? shipment?.collectedDateTime
      : releaseDate;

  if (!endDate) return null;

  if (!startDate) {
    logError("invoices:InvoiceUtils:getShipmentStorageWindow", {
      message: `Missing dates for ${frequency} storage billing`,
      shipmentId: shipment?.id,
      direction,
      startDate,
      endDate,
      placedDateTime: shipment?.placedDateTime,
      placementDateTime: shipment?.placementDateTime,
      releaseDateTime: shipment?.releaseDateTime,
      departureDateTime: shipment?.departureDateTime,
      collectedDateTime: shipment?.collectedDateTime,
    });
    return null;
  }

  return { startDate, endDate };
};

const getDepletionStorageWindow = (inventory) => {
  const startDate = inventory?.receivedAt;
  const endDate = inventory?.depletedAt;

  if (!startDate || !endDate) {
    logError("invoices:InvoiceUtils:getDepletionStorageWindow", {
      message: "Missing dates for ON_DEPLETION storage billing",
      inventoryId: inventory?.id,
      receivedAt: inventory?.receivedAt,
      depletedAt: inventory?.depletedAt,
    });
    return null;
  }

  return { startDate, endDate };
};

const isInventoryDepleted = (inventory, outbound) => {
  if (inventory?.status !== InventoryStatus.SHIPPED) return false;
  const lineItems = inventory?.shipmentLineItems?.items;
  if (!lineItems?.length) return false;
  const sorted = [...lineItems].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );
  return sorted[0]?.outboundShipmentId === outbound?.id;
};

const getOutOfStockBaseQuantity = (inventory) =>
  Number(inventory?.quantityReceived ?? inventory?.quantityAdvised ?? 0);

const getStorageTierServiceNote = (
  rule,
  quantity,
  unitCost,
  shipment,
  tier,
) => {
  const baseNote = getServiceNote(rule, quantity, unitCost, shipment);
  if (!tier) return baseNote;

  const tierDays = Number(tier?.days) || 0;
  const dayLabel = tierDays === 1 ? "day" : "days";
  const dailyRate = Number(tier?.unitCost);
  const dailyRateNote = Number.isFinite(dailyRate)
    ? ` @ $${trimDecimal(dailyRate, 2)}/day`
    : "";
  return `${baseNote} - Tier ${Number(tier?.tierIndex) + 1}: ${tierDays} ${dayLabel}${dailyRateNote}`;
};

/**
 * A PRORATED_DAILY storage line bills units × days × daily rate. On the
 * invoice it reads as unit-days at the daily rate (11 cars stored 106 days in
 * all is 106 CAR @ $6), so the rate shown is the one the customer agreed to
 * and quantity × unit cost still equals the amount, which the QuickBooks
 * export and the charges editor both recompute from.
 *
 * Returns the unit-days, or null when they do not reproduce the amount (a
 * min/max charge moved it, or the days are unknown); the caller then keeps the
 * averaged unit cost, amount ÷ units.
 */
const getProratedUnitDays = (rule, unitDays, dailyRate, amount) => {
  if (rule?.feeAccrual !== FeeAccrual.PRORATED_DAILY) return null;

  const quantity = trimDecimal(Number(unitDays) || 0);
  const rate = Number(dailyRate);
  if (quantity <= 0 || !Number.isFinite(rate) || rate <= 0) return null;

  return trimDecimal(quantity * rate, 2) === trimDecimal(Number(amount) || 0, 2)
    ? quantity
    : null;
};

/**
 * Note for a unit-day storage line. It names the units actually stored next
 * to the unit-days they add up to, since the quantity column now counts
 * unit-days: "Railcar Storage - 11 CAR, 106 CAR-days @ $6.00/CAR/day".
 */
const getUnitDayServiceNote = ({
  rule,
  units,
  unitDays,
  dailyRate,
  shipment,
  tier,
  label,
}) => {
  const uom = rule.billingUOM;
  const dayLabel = unitDays === 1 ? "day" : "days";
  const parts = [
    label || rule.service?.description || rule.service?.name || "",
    `${units} ${uom}, ${unitDays} ${uom}-${dayLabel} @ ${formatCurrency(dailyRate)}/${uom}/day`,
  ];
  if (tier) parts.push(`Tier ${Number(tier.tierIndex) + 1}`);

  return [...parts, ...getShipmentNoteParts(shipment)].join(" - ");
};

const buildStorageInvoiceLineItemsFromFees = ({
  fees,
  billableDays,
  rule,
  serviceDate,
  shipment,
  shipmentIdField,
  shipmentId,
}) => {
  if (!rule?.service || typeof fees?.amount !== "number") return [];

  const price = getUnitPrice(rule.service, rule.billingUOM, 0);
  if (price == null) return [];

  const billedQuantity = trimDecimal(Number(fees?.billedQuantity) || 0);
  const amount = Number(fees?.amount) || 0;
  if (billedQuantity <= 0 || amount <= 0) return [];

  // A tier's days are per unit; without tiers every unit was billed for all
  // of the line's billable days.
  const feeTiers =
    Array.isArray(fees?.tierBreakdown) && fees.tierBreakdown.length
      ? fees.tierBreakdown
      : [
          {
            billedQuantity,
            unitCost: price,
            amount,
            days: billableDays,
          },
        ];

  return feeTiers
    .map((tier) => {
      const units = trimDecimal(Number(tier?.billedQuantity) || billedQuantity);
      const nominalUnitCost = trimDecimal(Number(tier?.unitCost) || price, 2);
      const tierAmount = Number(tier?.amount) || 0;
      if (units <= 0 || tierAmount <= 0) return null;

      const tierNote =
        feeTiers.length > 1 ? { ...tier, unitCost: nominalUnitCost } : null;
      const unitDays = getProratedUnitDays(
        rule,
        units * (Number(tier?.days) || 0),
        nominalUnitCost,
        tierAmount,
      );
      const quantity = unitDays ?? units;
      const unitCost = unitDays
        ? nominalUnitCost
        : rule?.feeAccrual === FeeAccrual.PRORATED_DAILY
          ? trimDecimal(tierAmount / units, 6)
          : nominalUnitCost;

      const itemTax = tierAmount * (rule.service.taxRate?.taxRate || 0) * 0.01;
      const itemTotal = tierAmount + itemTax;

      return {
        serviceId: rule.service.id,
        serviceDate,
        serviceNote: unitDays
          ? getUnitDayServiceNote({
              rule,
              units,
              unitDays,
              dailyRate: nominalUnitCost,
              shipment,
              tier: tierNote,
            })
          : getStorageTierServiceNote(
              rule,
              quantity,
              unitCost,
              shipment,
              tierNote,
            ),
        [shipmentIdField]: shipmentId,
        event: ChargeEvent.STORAGE,
        quantity,
        units: rule.billingUOM,
        unitCost,
        amount: trimDecimal(tierAmount, 2),
        taxRate: rule.service.taxRate,
        itemTax: trimDecimal(itemTax, 2),
        itemTotal: trimDecimal(itemTotal, 2),
      };
    })
    .filter(Boolean);
};

const buildShipmentStorageLineItemForInbound = ({
  inbound,
  rule,
  quantityOverride,
}) => {
  // manual Storage rule execution
  if (!rule?.service || !isShipmentStorageRule(rule)) return [];
  debugger;
  const billingAccountId = getBillingAccountId(inbound);
  const leasedZoneMap = getLeasedZoneMap(billingAccountId);
  const shouldSkipForLeasedZone = shouldSkipEventDrivenStorageForLeasedZones({
    record: inbound,
    inventories: inbound?.inventories?.items || [],
    accountId: billingAccountId,
    leasedZoneMap,
  });
  if (shouldSkipForLeasedZone) return [];

  const storageWindow = getShipmentStorageWindow(
    inbound,
    "inbound",
    rule.frequency,
  );
  if (!storageWindow) return [];

  const { startDate, endDate } = storageWindow;
  const serviceDate = endDate;
  const isShipmentLevelRule =
    Boolean(rule?.carrierType) || isShipmentUom(rule.billingUOM);

  if (isShipmentLevelRule) {
    const storageInventory = {
      ...inbound,
      status: InventoryStatus.SHIPPED,
      receivedAt: startDate,
      depletedAt: endDate,
      inventoryUOM: inbound?.totalUOM || rule.billingUOM,
    };

    const billableItem = buildShipmentLevelStorageBillableItem(
      storageInventory,
      rule,
      {
        startDate,
        endDate,
        shippedDate: endDate,
      },
    );

    if (!billableItem || typeof billableItem.fees !== "number") return [];

    return buildStorageInvoiceLineItemsFromFees({
      fees: {
        amount: billableItem.fees,
        billedQuantity: billableItem.billedQuantity,
        tierBreakdown: billableItem.tierBreakdown,
      },
      billableDays: billableItem.billableDays,
      rule,
      serviceDate,
      shipment: inbound,
      shipmentIdField: "inboundShipmentId",
      shipmentId: inbound.id,
    });
  }

  const quantity = Number(quantityOverride ?? 0);
  if (!quantity || quantity <= 0) return null;

  const freeDays = calculateStorageFreeDays(rule, startDate);
  const billableDays = calculateBillableDays(
    startDate,
    endDate,
    startDate,
    endDate,
    rule.feeAccrual,
    freeDays,
  );
  const storageInventory = {
    ...inbound,
    status: InventoryStatus.SHIPPED,
    receivedAt: startDate,
    depletedAt: endDate,
    inventoryUOM: rule.billingUOM,
  };
  const billableItem = {
    quantity,
    inventoryUOM: rule.billingUOM,
    weight: 0,
    weightUOM: null,
    dateShipped: endDate,
    billableDays,
  };
  const fees = calculateStorageFees(
    storageInventory,
    billableItem,
    {
      startDate,
      endDate,
      freeDays,
      accrual: rule.feeAccrual,
    },
    rule,
  );
  return buildStorageInvoiceLineItemsFromFees({
    fees,
    billableDays,
    rule,
    serviceDate,
    shipment: inbound,
    shipmentIdField: "inboundShipmentId",
    shipmentId: inbound.id,
  });
};

const buildShipmentStorageLineItemsForInbound = (inbound, inboundRules) => {
  const storageRules = sortChargeRules(
    (inboundRules || []).filter(
      (rule) =>
        rule?.execution === BillingAutomation.AUTO &&
        isShipmentStorageRule(rule),
    ),
  );

  debugger;
  if (!storageRules.length) return [];

  const rulesByServiceMap = new Map();
  storageRules.forEach((rule) => {
    if (!rulesByServiceMap.has(rule.serviceId)) {
      rulesByServiceMap.set(rule.serviceId, []);
    }
    rulesByServiceMap.get(rule.serviceId).push(rule);
  });

  const lineItems = [];

  for (const [, rules] of rulesByServiceMap) {
    const selectedRule = findApplicableRule(inbound, rules);
    if (!selectedRule?.service) continue;
    const storageLineItems = buildShipmentStorageLineItemForInbound({
      inbound,
      rule: selectedRule,
    });
    if (storageLineItems.length) lineItems.push(...storageLineItems);
  }

  return lineItems;
};

const buildShipmentStorageLineItemForOutbound = ({
  outbound,
  rule,
  quantityOverride,
}) => {
  if (!rule?.service || !isShipmentStorageRule(rule)) return [];
  const billingAccountId = getBillingAccountId(outbound);
  const leasedZoneMap = getLeasedZoneMap(billingAccountId);
  const shouldSkipForLeasedZone = shouldSkipEventDrivenStorageForLeasedZones({
    record: outbound,
    inventories: getUniqueOutboundInventories(outbound),
    accountId: billingAccountId,
    leasedZoneMap,
  });
  if (shouldSkipForLeasedZone) return [];

  const storageWindow = getShipmentStorageWindow(
    outbound,
    "outbound",
    rule.frequency,
  );
  if (!storageWindow) return [];

  const { startDate, endDate } = storageWindow;
  const serviceDate = endDate;
  const isShipmentLevelRule =
    Boolean(rule?.carrierType) || isShipmentUom(rule.billingUOM);

  if (isShipmentLevelRule) {
    const storageInventory = {
      ...outbound,
      status: InventoryStatus.SHIPPED,
      receivedAt: startDate,
      depletedAt: endDate,
      inventoryUOM: outbound?.totalUOM || rule.billingUOM,
    };

    const billableItem = buildShipmentLevelStorageBillableItem(
      storageInventory,
      rule,
      {
        startDate,
        endDate,
        shippedDate: endDate,
      },
    );

    if (!billableItem || typeof billableItem.fees !== "number") return [];

    return buildStorageInvoiceLineItemsFromFees({
      fees: {
        amount: billableItem.fees,
        billedQuantity: billableItem.billedQuantity,
        tierBreakdown: billableItem.tierBreakdown,
      },
      billableDays: billableItem.billableDays,
      rule,
      serviceDate,
      shipment: outbound,
      shipmentIdField: "outboundShipmentId",
      shipmentId: outbound.id,
    });
  }

  const quantity = Number(quantityOverride ?? 0);
  if (!quantity || quantity <= 0) return null;

  const freeDays = calculateStorageFreeDays(rule, startDate);
  const billableDays = calculateBillableDays(
    startDate,
    endDate,
    startDate,
    endDate,
    rule.feeAccrual,
    freeDays,
  );
  const storageInventory = {
    ...outbound,
    status: InventoryStatus.SHIPPED,
    receivedAt: startDate,
    depletedAt: endDate,
    inventoryUOM: rule.billingUOM,
  };
  const billableItem = {
    quantity,
    inventoryUOM: rule.billingUOM,
    weight: 0,
    weightUOM: null,
    dateShipped: endDate,
    billableDays,
  };
  const fees = calculateStorageFees(
    storageInventory,
    billableItem,
    {
      startDate,
      endDate,
      freeDays,
      accrual: rule.feeAccrual,
    },
    rule,
  );
  return buildStorageInvoiceLineItemsFromFees({
    fees,
    billableDays,
    rule,
    serviceDate,
    shipment: outbound,
    shipmentIdField: "outboundShipmentId",
    shipmentId: outbound.id,
  });
};

const buildShipmentStorageLineItemsForOutbound = (outbound, outboundRules) => {
  const storageRules = sortChargeRules(
    (outboundRules || []).filter(
      (rule) =>
        rule?.execution === BillingAutomation.AUTO &&
        isShipmentStorageRule(rule),
    ),
  );

  if (!storageRules.length) return [];

  const rulesByServiceMap = new Map();
  storageRules.forEach((rule) => {
    if (!rulesByServiceMap.has(rule.serviceId)) {
      rulesByServiceMap.set(rule.serviceId, []);
    }
    rulesByServiceMap.get(rule.serviceId).push(rule);
  });

  const lineItems = [];

  for (const [, rules] of rulesByServiceMap) {
    const selectedRule = findApplicableRule(outbound, rules);
    if (!selectedRule?.service) continue;
    const storageLineItems = buildShipmentStorageLineItemForOutbound({
      outbound,
      rule: selectedRule,
    });
    if (storageLineItems.length) lineItems.push(...storageLineItems);
  }

  return lineItems;
};

const buildOutOfStockStorageLineItemForOutbound = ({
  outbound,
  inventory,
  rule,
  quantityOverride,
}) => {
  if (
    !rule?.service ||
    !inventory ||
    !isOutOfStockStorageRule(rule) ||
    !isInventoryDepleted(inventory, outbound)
  ) {
    return [];
  }

  const storageWindow = getDepletionStorageWindow(inventory);
  if (!storageWindow) return [];

  const { startDate, endDate } = storageWindow;
  const serviceDate = endDate;
  const isShipmentLevelRule =
    Boolean(rule?.carrierType) || isShipmentUom(rule.billingUOM);

  if (isShipmentLevelRule) {
    const storageInventory = {
      ...inventory,
      status: InventoryStatus.SHIPPED,
      receivedAt: startDate,
      depletedAt: endDate,
      inventoryUOM: inventory?.inventoryUOM || rule.billingUOM,
    };

    const billableItem = buildShipmentLevelStorageBillableItem(
      storageInventory,
      rule,
      {
        startDate,
        endDate,
        shippedDate: endDate,
      },
    );

    if (!billableItem || typeof billableItem.fees !== "number") return [];

    return buildStorageInvoiceLineItemsFromFees({
      fees: {
        amount: billableItem.fees,
        billedQuantity: billableItem.billedQuantity,
        tierBreakdown: billableItem.tierBreakdown,
      },
      billableDays: billableItem.billableDays,
      rule,
      serviceDate,
      shipment: outbound,
      shipmentIdField: "outboundShipmentId",
      shipmentId: outbound.id,
    });
  }

  const quantity = Number(
    quantityOverride ?? getOutOfStockBaseQuantity(inventory),
  );
  if (!quantity || quantity <= 0) return null;

  const freeDays = calculateStorageFreeDays(rule, startDate);
  const billableDays = calculateBillableDays(
    startDate,
    endDate,
    startDate,
    endDate,
    rule.feeAccrual,
    freeDays,
  );
  const storageInventory = {
    ...inventory,
    status: InventoryStatus.SHIPPED,
    receivedAt: startDate,
    depletedAt: endDate,
    inventoryUOM: inventory?.inventoryUOM || rule.billingUOM,
  };
  const billableItem = {
    quantity,
    inventoryUOM:
      inventory?.inventoryUOM || outbound?.totalUOM || rule.billingUOM,
    weight: Number(inventory?.weight) || 0,
    weightUOM: inventory?.weightUOM || null,
    dateShipped: endDate,
    billableDays,
  };
  const fees = calculateStorageFees(
    storageInventory,
    billableItem,
    {
      startDate,
      endDate,
      freeDays,
      accrual: rule.feeAccrual,
    },
    rule,
  );
  return buildStorageInvoiceLineItemsFromFees({
    fees,
    billableDays,
    rule,
    serviceDate,
    shipment: outbound,
    shipmentIdField: "outboundShipmentId",
    shipmentId: outbound.id,
  });
};

const buildOutOfStockStorageLineItemsForOutbound = (
  outbound,
  outboundRules,
) => {
  const storageRules = sortChargeRules(
    (outboundRules || []).filter(
      (rule) =>
        rule?.execution === BillingAutomation.AUTO &&
        isOutOfStockStorageRule(rule),
    ),
  );
  if (!storageRules.length) return [];

  const billingAccountId = getBillingAccountId(outbound);
  const leasedZoneMap = getLeasedZoneMap(billingAccountId);
  const depletedInventories = trimLeasedZoneInventories(
    getUniqueOutboundInventories(outbound),
    billingAccountId,
    leasedZoneMap,
  ).filter((inventory) => isInventoryDepleted(inventory, outbound));

  if (!depletedInventories.length) return [];

  const lineItems = [];
  for (const inventory of depletedInventories) {
    const storageShipment = {
      ...outbound,
      areaId: inventory?.areaId ?? outbound?.areaId,
      zoneId: inventory?.zoneId ?? outbound?.zoneId,
      reloadYardCommodityId:
        inventory?.reloadYardCommodityId ?? outbound?.reloadYardCommodityId,
    };
    const rulesByServiceMap = new Map();
    storageRules.forEach((rule) => {
      if (!rulesByServiceMap.has(rule.serviceId)) {
        rulesByServiceMap.set(rule.serviceId, []);
      }
      rulesByServiceMap.get(rule.serviceId).push(rule);
    });

    for (const [, rules] of rulesByServiceMap) {
      const selectedRule = findApplicableRule(storageShipment, rules);
      if (!selectedRule?.service) continue;
      const storageLineItems = buildOutOfStockStorageLineItemForOutbound({
        outbound,
        inventory,
        rule: selectedRule,
      });
      if (storageLineItems.length) lineItems.push(...storageLineItems);
    }
  }

  return lineItems;
};

export const createLineItemsForInbound = (inbound, inboundRules) => {
  if (!inbound) return [];
  debugger;

  const inventories = inbound.inventories?.items || [];
  const carrierType = inbound.carrierType;
  const carrierEquipmentTypeId = inbound.carrierEquipmentTypeId;
  const serviceDate = inbound?.releaseDateTime || new Date();
  const storageLineItems = buildShipmentStorageLineItemsForInbound(
    inbound,
    inboundRules,
  );

  // Filter to AUTO rules and sort by specificity (most specific first)
  const autoRules = (inboundRules || []).filter(
    (rule) =>
      rule.execution === BillingAutomation.AUTO &&
      rule.event === ChargeEvent.INBOUND,
  );
  const sortedAutoRules = sortChargeRules(autoRules);

  if (!sortedAutoRules.length) {
    const manualItems = buildManualLineItemsForInbound(inbound, serviceDate);
    return [...storageLineItems, ...manualItems];
  }

  // Step 1: Group ALL autoRules by serviceId and find the most specific applicable rule per service
  const byServiceMap = new Map();
  sortedAutoRules.forEach((r) => {
    if (!byServiceMap.has(r.serviceId)) byServiceMap.set(r.serviceId, []);
    byServiceMap.get(r.serviceId).push(r);
  });

  const chosenRulesByServiceMap = new Map();
  for (const [serviceId, rules] of byServiceMap) {
    // Find the most specific applicable rule for this service (across all UOMs)
    const selectedRule = findApplicableRule(inbound, rules);
    if (selectedRule?.service) {
      chosenRulesByServiceMap.set(serviceId, selectedRule);
    }
  }

  if (!chosenRulesByServiceMap.size) {
    const manualItems = buildManualLineItemsForInbound(inbound, serviceDate);
    return [...storageLineItems, ...manualItems];
  }

  // Step 2: Separate chosen rules by UOM type and apply at appropriate level
  const shipmentLevelRules = [];
  const itemLevelRules = [];

  for (const [, rule] of chosenRulesByServiceMap) {
    if (isShipmentUom(rule.billingUOM)) {
      shipmentLevelRules.push(rule);
    } else {
      itemLevelRules.push(rule);
    }
  }

  const lineItems = [];

  // Step 3a: Process shipment-level rules (CAR/TL) - one line per service
  for (const rule of shipmentLevelRules) {
    const service = rule.service;
    if (!service) continue;

    const price = getUnitPrice(service, rule.billingUOM, 0);
    if (price == null) continue;

    const quantity = 1;
    const baseAmount = quantity * price;
    const amount = applyChargeConstraints(
      baseAmount,
      rule.minCharge,
      rule.maxCharge,
    );
    const itemTax = amount * (service.taxRate?.taxRate || 0) * 0.01;
    const itemTotal = amount + itemTax;

    lineItems.push({
      serviceId: service.id,
      serviceDate,
      serviceNote: getServiceNote(rule, quantity, price, inbound),
      inboundShipmentId: inbound.id,
      event: ChargeEvent.INBOUND,
      quantity,
      units: rule.billingUOM,
      unitCost: price,
      amount: trimDecimal(amount, 2),
      taxRate: service.taxRate,
      itemTax: trimDecimal(itemTax, 2),
      itemTotal: trimDecimal(itemTotal, 2),
    });
  }

  // Step 3b: Process item-level rules (non-CAR/TL) - aggregate across inventories
  if (itemLevelRules.length && inventories.length > 0) {
    // Group item-level rules by (serviceId + billingUOM) to avoid mixing units
    const itemRuleGroups = new Map();
    for (const rule of itemLevelRules) {
      const key = `${rule.serviceId}_${rule.billingUOM}`;
      if (!itemRuleGroups.has(key)) {
        itemRuleGroups.set(key, {
          rule,
          baseAmount: 0,
          totalQuantity: 0,
        });
      }
    }

    // Aggregate amounts across inventories
    for (const inv of inventories) {
      const invSafe = inv || {};
      const augmented = {
        ...invSafe,
        carrierType,
        carrierEquipmentTypeId,
      };

      for (const [, group] of itemRuleGroups) {
        const { rule } = group;
        if (!rule.service) continue;

        // Check if this rule applies to this specific inventory
        // (item-level rules need to match per inventory for area/zone specificity)
        if (!doesRuleApply(rule, augmented)) continue;

        const price = getUnitPrice(rule.service, rule.billingUOM, 0);
        if (price == null) continue;

        // Calculate quantity for this inventory
        let quantity = 0;
        const invQty = Number(inv.quantityReceived || inv.quantityAdvised || 0);
        const invUOM = inv.inventoryUOM || inbound?.totalUOM;

        if (invQty && invUOM === rule.billingUOM) {
          quantity = invQty;
        } else {
          if (invQty && invUOM) {
            const conv = getConversionValue(
              inv,
              invQty,
              rule.billingUOM,
              invUOM,
              null,
            );
            if (conv != null) quantity = conv;
          }
          // Optional: convert inventory total weight to target UOM (only if invQty > 0)
          if (!quantity && invQty > 0 && inv.weight && inv.weightUOM) {
            const totalWeight = inv.weight * invQty;
            const convW = convertUnits(
              totalWeight,
              inv.weightUOM,
              rule.billingUOM,
              inv.product,
            );
            if (!convW.error) quantity = convW.value;
          }
        }

        if (quantity > 0) {
          group.baseAmount += quantity * price;
          group.totalQuantity += quantity;
        }
      }
    }

    // Create line items for item-level rules
    for (const [, group] of itemRuleGroups) {
      const { rule, baseAmount, totalQuantity } = group;

      const service = rule.service;
      if (!service) continue;

      const price = getUnitPrice(service, rule.billingUOM, 0);
      if (price == null) continue;

      // Apply min/max on grouped amount (pre-tax)
      const amount = applyChargeConstraints(
        baseAmount,
        rule.minCharge,
        rule.maxCharge,
      );
      if (amount == null || amount <= 0) continue;

      const quantity = trimDecimal(totalQuantity || 0);
      const itemTax = amount * (service.taxRate?.taxRate || 0) * 0.01;
      const itemTotal = amount + itemTax;

      lineItems.push({
        serviceId: service.id,
        serviceDate,
        serviceNote: getServiceNote(rule, quantity, price, inbound),
        inboundShipmentId: inbound.id,
        event: ChargeEvent.INBOUND,
        quantity,
        units: rule.billingUOM,
        unitCost: price,
        amount: trimDecimal(amount, 2),
        taxRate: service.taxRate,
        itemTax: trimDecimal(itemTax, 2),
        itemTotal: trimDecimal(itemTotal, 2),
      });
    }
  }

  // Add manual line items
  const manualItems = buildManualLineItemsForInbound(inbound, serviceDate);
  return [...lineItems, ...storageLineItems, ...manualItems];
};

export const createLineItemsForOutbound = (outbound, outboundRules) => {
  if (!outbound) return [];

  const shipmentLineItems = outbound.shipmentLineItems?.items || [];
  const carrierType = outbound.carrierType;
  const carrierEquipmentTypeId = outbound.carrierEquipmentTypeId;
  const serviceDate =
    outbound?.departureDateTime || outbound?.collectedDateTime || new Date();
  const shipmentStorageLineItems = buildShipmentStorageLineItemsForOutbound(
    outbound,
    outboundRules,
  );
  const outOfStockStorageLineItems = buildOutOfStockStorageLineItemsForOutbound(
    outbound,
    outboundRules,
  );

  // Filter to AUTO rules and sort by specificity (most specific first)
  const autoRules = sortChargeRules(
    (outboundRules || []).filter(
      (r) =>
        r.execution === BillingAutomation.AUTO &&
        r.event === ChargeEvent.OUTBOUND,
    ),
  );

  if (!autoRules.length) {
    const manualItems = buildManualLineItemsForOutbound(outbound, serviceDate);
    return [
      ...shipmentStorageLineItems,
      ...outOfStockStorageLineItems,
      ...manualItems,
    ];
  }

  // Step 1: Group ALL autoRules by serviceId and find the most specific applicable rule per service
  const byService = new Map();
  autoRules.forEach((r) => {
    if (!byService.has(r.serviceId)) byService.set(r.serviceId, []);
    byService.get(r.serviceId).push(r);
  });

  const chosenRulesByService = new Map();
  for (const [serviceId, rules] of byService) {
    // Find the most specific applicable rule for this service (across all UOMs)
    const selectedRule = findApplicableRule(outbound, rules);
    if (selectedRule?.service) {
      chosenRulesByService.set(serviceId, selectedRule);
    }
  }

  if (!chosenRulesByService.size) {
    const manualItems = buildManualLineItemsForOutbound(outbound, serviceDate);
    return [
      ...shipmentStorageLineItems,
      ...outOfStockStorageLineItems,
      ...manualItems,
    ];
  }

  // Step 2: Separate chosen rules by UOM type and apply at appropriate level
  const shipmentLevelRules = [];
  const itemLevelRules = [];

  for (const [, rule] of chosenRulesByService) {
    if (isShipmentUom(rule.billingUOM)) {
      shipmentLevelRules.push(rule);
    } else {
      itemLevelRules.push(rule);
    }
  }

  const lineItems = [];

  // Step 3a: Process shipment-level rules (CAR/TL) - one line per service
  for (const rule of shipmentLevelRules) {
    const service = rule.service;
    if (!service) continue;

    const price = getUnitPrice(service, rule.billingUOM, 0);
    if (price == null) continue;

    const quantity = 1;
    const baseAmount = quantity * price;
    const amount = applyChargeConstraints(
      baseAmount,
      rule.minCharge,
      rule.maxCharge,
    );
    const itemTax = amount * (service.taxRate?.taxRate || 0) * 0.01;
    const itemTotal = amount + itemTax;

    lineItems.push({
      serviceId: service.id,
      serviceDate,
      serviceNote: getServiceNote(rule, quantity, price, outbound),
      outboundShipmentId: outbound.id,
      event: ChargeEvent.OUTBOUND,
      quantity,
      units: rule.billingUOM,
      unitCost: price,
      amount: trimDecimal(amount, 2),
      taxRate: service.taxRate,
      itemTax: trimDecimal(itemTax, 2),
      itemTotal: trimDecimal(itemTotal, 2),
    });
  }

  // Step 3b: Process item-level rules (non-CAR/TL) - aggregate across shipment line items
  if (itemLevelRules.length && shipmentLineItems.length > 0) {
    // Group item-level rules by (serviceId + billingUOM) to avoid mixing units
    const itemRuleGroups = new Map();
    for (const rule of itemLevelRules) {
      const key = `${rule.serviceId}_${rule.billingUOM}`;
      if (!itemRuleGroups.has(key)) {
        itemRuleGroups.set(key, {
          rule,
          baseAmount: 0,
          totalQuantity: 0,
        });
      }
    }

    // Aggregate amounts across shipment line items
    for (const sli of shipmentLineItems) {
      const inv = sli?.inventory || {};
      const augmented = {
        ...inv,
        carrierType,
        carrierEquipmentTypeId,
      };

      for (const [, group] of itemRuleGroups) {
        const { rule } = group;
        if (!rule.service) continue;

        // Check if this rule applies to this specific inventory/item
        // (item-level rules need to match per inventory for area/zone specificity)
        if (!doesRuleApply(rule, augmented)) continue;

        const price = getUnitPrice(rule.service, rule.billingUOM, 0);
        if (price == null) continue;

        // Calculate quantity for this shipment line item
        let quantity = 0;
        const invQty = Number(sli.totalQuantityToPick || 0);
        const invUOM = inv?.inventoryUOM || sli.units || outbound.totalUOM;

        if (invQty && invUOM === rule.billingUOM) {
          quantity = invQty;
        } else {
          if (invQty && invUOM) {
            const conv = getConversionValue(
              {
                ...inv,
                inventoryUOM: invUOM,
                reloadYardCommodityId:
                  inv?.reloadYardCommodityId || outbound.reloadYardCommodityId,
              },
              invQty,
              rule.billingUOM,
              invUOM,
              null,
            );
            if (conv != null) quantity = conv;
          }
          // Optional: convert total inventory weight to target UOM (only if invQty > 0)
          if (!quantity && invQty > 0 && inv?.weight && inv?.weightUOM) {
            const totalWeight = inv.weight * invQty;
            const convW = convertUnits(
              totalWeight,
              inv.weightUOM,
              rule.billingUOM,
              inv?.product,
            );
            if (!convW.error) quantity = convW.value;
          }
        }

        if (quantity > 0) {
          group.baseAmount += quantity * price;
          group.totalQuantity += quantity;
        }
      }
    }

    // Create line items for item-level rules
    for (const [, group] of itemRuleGroups) {
      const { rule, baseAmount, totalQuantity } = group;

      const service = rule.service;
      if (!service) continue;

      const price = getUnitPrice(service, rule.billingUOM, 0);
      if (price == null) continue;

      // Apply min/max on grouped amount (pre-tax)
      const amount = applyChargeConstraints(
        baseAmount,
        rule.minCharge,
        rule.maxCharge,
      );
      if (amount == null || amount <= 0) continue;

      const quantity = trimDecimal(totalQuantity || 0);
      const itemTax = amount * (service.taxRate?.taxRate || 0) * 0.01;
      const itemTotal = amount + itemTax;

      lineItems.push({
        serviceId: service.id,
        serviceDate,
        serviceNote: getServiceNote(rule, quantity, price, outbound),
        outboundShipmentId: outbound.id,
        event: ChargeEvent.OUTBOUND,
        quantity,
        units: rule.billingUOM,
        unitCost: price,
        amount: trimDecimal(amount, 2),
        taxRate: service.taxRate,
        itemTax: trimDecimal(itemTax, 2),
        itemTotal: trimDecimal(itemTotal, 2),
      });
    }
  }

  // Add manual line items
  const manualItems = buildManualLineItemsForOutbound(outbound, serviceDate);
  return [
    ...lineItems,
    ...shipmentStorageLineItems,
    ...outOfStockStorageLineItems,
    ...manualItems,
  ];
};

const buildManualLineItemsForInbound = (inbound, serviceDate) => {
  const manualCharges = inbound?.manualCharges?.items || [];
  if (!manualCharges.length) return [];

  const items = [];

  manualCharges.forEach((wc) => {
    const rule = wc.chargeRule;
    const service = rule?.service;
    const quantity = Number(wc.quantity || 0);

    if (
      !rule ||
      !service ||
      !quantity ||
      quantity <= 0 ||
      rule.execution !== BillingAutomation.MANUAL
    )
      return;

    if (isShipmentStorageRule(rule)) {
      const storageLineItems = buildShipmentStorageLineItemForInbound({
        inbound,
        rule,
        quantityOverride: quantity,
      });
      if (storageLineItems.length) items.push(...storageLineItems);
      return;
    }

    const price = getUnitPrice(service, rule.billingUOM, 0);
    if (price == null) return;

    const baseAmount = quantity * price;
    const amount = applyChargeConstraints(
      baseAmount,
      rule.minCharge,
      rule.maxCharge,
    );
    const itemTax = amount * (service.taxRate?.taxRate || 0) * 0.01;
    const itemTotal = amount + itemTax;

    items.push({
      serviceId: service.id,
      serviceDate,
      serviceNote: getServiceNote(rule, quantity, price, inbound),
      inboundShipmentId: inbound.id,
      event: rule.event ?? ChargeEvent.INBOUND,
      quantity: trimDecimal(quantity),
      units: rule.billingUOM,
      unitCost: price,
      amount: trimDecimal(amount, 2),
      taxRate: service.taxRate,
      itemTax: trimDecimal(itemTax, 2),
      itemTotal: trimDecimal(itemTotal, 2),
    });
  });

  return items;
};

const buildManualLineItemsForOutbound = (outbound, serviceDate) => {
  const manualCharges = outbound?.manualCharges?.items || [];
  if (!manualCharges.length) return [];

  const items = [];
  const depletedInventories = [
    ...new Map(
      (outbound?.shipmentLineItems?.items || [])
        .map((shipmentLineItem) => shipmentLineItem?.inventory)
        .filter((inventory) => inventory?.id)
        .map((inventory) => [inventory.id, inventory]),
    ).values(),
  ].filter((inventory) => isInventoryDepleted(inventory, outbound));

  manualCharges.forEach((wc) => {
    const rule = wc.chargeRule;
    const service = rule?.service;
    const quantity = Number(wc.quantity || 0);

    if (
      !rule ||
      !service ||
      !quantity ||
      quantity <= 0 ||
      rule.execution !== BillingAutomation.MANUAL
    )
      return;

    if (isShipmentStorageRule(rule)) {
      const storageLineItems = buildShipmentStorageLineItemForOutbound({
        outbound,
        rule,
        quantityOverride: quantity,
      });
      if (storageLineItems.length) items.push(...storageLineItems);
      return;
    }

    if (isOutOfStockStorageRule(rule)) {
      depletedInventories.forEach((inventory) => {
        const storageLineItems = buildOutOfStockStorageLineItemForOutbound({
          outbound,
          inventory,
          rule,
          quantityOverride: quantity,
        });
        if (storageLineItems.length) items.push(...storageLineItems);
      });
      return;
    }

    const price = getUnitPrice(service, rule.billingUOM, 0);
    if (price == null) return;

    const baseAmount = quantity * price;
    const amount = applyChargeConstraints(
      baseAmount,
      rule.minCharge,
      rule.maxCharge,
    );
    const itemTax = amount * (service.taxRate?.taxRate || 0) * 0.01;
    const itemTotal = amount + itemTax;

    items.push({
      serviceId: service.id,
      serviceDate,
      serviceNote: getServiceNote(rule, quantity, price, outbound),
      outboundShipmentId: outbound.id,
      event: rule.event ?? ChargeEvent.OUTBOUND,
      quantity: trimDecimal(quantity),
      units: rule.billingUOM,
      unitCost: price,
      amount: trimDecimal(amount, 2),
      taxRate: service.taxRate,
      itemTax: trimDecimal(itemTax, 2),
      itemTotal: trimDecimal(itemTotal, 2),
    });
  });

  return items;
};

export const setupInboundInvoice = (
  account,
  inbound,
  inboundRules,
  services,
) => {
  // Build base invoice + default line items from fresh data
  const invoiceData = {
    ...createInvoiceBase(account, inbound, null),
    event: ChargeEvent.INBOUND,
  };
  invoiceData.lineItems = setupInboundILineItems(
    inbound,
    inboundRules,
    services,
  );
  return invoiceData;
};

export const setupInboundILineItems = (inbound, inboundRules, services) => {
  if (inboundRules.length) {
    // Use rule-driven behavior
    const lineItems = createLineItemsForInbound(inbound, inboundRules);
    return { items: lineItems };
  }

  return { items: [] };
};

export const setupOutboundInvoice = (
  account,
  outbound,
  outboundRules,
  services,
) => {
  // Build base invoice + default line items from fresh data
  const invoiceData = {
    ...createInvoiceBase(account, null, outbound),
    event: ChargeEvent.OUTBOUND,
  };
  invoiceData.lineItems = setupOutboundILineItems(
    outbound,
    outboundRules,
    services,
  );
  return invoiceData;
};

export const setupOutboundILineItems = (outbound, outboundRules, services) => {
  if (outboundRules.length) {
    // Use rule-driven behavior
    const lineItems = createLineItemsForOutbound(outbound, outboundRules);
    return { items: lineItems };
  }

  return { items: [] };
};

export const setupStorageInvoice = (
  account,
  serviceDate,
  dateRange,
  reportFile,
  billingData,
) => {
  // Build common base via createInvoiceBase
  const invoiceData = {
    ...createInvoiceBase(account, null, null),
    event: ChargeEvent.STORAGE,
    notes: `Storage invoice for ${dateRange}`,
  };
  if (reportFile) {
    invoiceData.attachments = [
      {
        src: URL.createObjectURL(reportFile),
        title: reportFile.name,
        rawFile: reportFile,
      },
    ];
  }

  const lineItems = buildStorageLineItems(billingData, serviceDate);
  invoiceData.lineItems = { items: lineItems };

  return invoiceData;
};

export const buildStorageLineItems = (billingData, serviceDate) => {
  if (!Array.isArray(billingData) || billingData.length === 0) return [];

  // Groups billableItems by (rule.id + UOM + tier) and returns one line per group
  const ruleGroups = new Map();

  billingData.forEach((inv) => {
    const billableItems = inv?.billableItems || [];
    billableItems.forEach((bi) => {
      const rule = bi?.appliedRule;
      if (!rule) return;
      if (!rule.id || !rule.billingUOM) return;

      const feeTiers =
        Array.isArray(bi?.tierBreakdown) && bi.tierBreakdown.length
          ? bi.tierBreakdown
          : [
              {
                tierIndex: -1,
                billedQuantity: bi?.billedQuantity,
                unitCost: getUnitPrice(rule.service, rule.billingUOM, 0),
                amount: bi?.fees,
                days: null,
              },
            ];

      feeTiers.forEach((tier) => {
        const fee = Number(tier?.amount);
        const billedQuantity = Number(tier?.billedQuantity);
        const unitCost = Number(tier?.unitCost);
        if (typeof fee !== "number" || fee < 0) return;
        if (typeof billedQuantity !== "number" || billedQuantity < 0) return;
        if (typeof unitCost !== "number" || unitCost < 0) return;

        const tierIndex = Number.isFinite(Number(tier?.tierIndex))
          ? Number(tier.tierIndex)
          : -1;
        const lineGroupKey = bi?.lineGroupKey || "";
        const key = `${rule.id}_${rule.billingUOM}_${lineGroupKey}_${tierIndex}_${unitCost}`;
        if (!ruleGroups.has(key)) {
          ruleGroups.set(key, {
            rule,
            tierIndex,
            unitCost,
            totalAmount: 0,
            totalQuantity: 0,
            totalDays: 0,
            unitDays: 0,
            serviceNote: bi?.serviceNote || "",
          });
        }
        const group = ruleGroups.get(key);
        group.totalAmount += fee;
        group.totalQuantity += billedQuantity;
        group.totalDays += Number(tier?.days) || 0;
        // A tier's days are per unit; without tiers every unit was billed for
        // the item's billable days.
        const unitDaysEach = tierIndex >= 0 ? tier?.days : bi?.billableDays;
        group.unitDays += billedQuantity * (Number(unitDaysEach) || 0);
      });
    });
  });

  const items = [];
  for (const [, group] of ruleGroups) {
    const {
      rule,
      totalAmount,
      totalQuantity,
      totalDays,
      tierIndex,
      unitCost,
      serviceNote,
    } = group;
    const service = rule?.service;
    if (!service) continue;

    const units = trimDecimal(totalQuantity || 0);
    const unitDays = getProratedUnitDays(
      rule,
      group.unitDays,
      unitCost,
      totalAmount,
    );
    const quantity = unitDays ?? units;
    const lineUnitCost = unitDays
      ? unitCost
      : rule?.feeAccrual === FeeAccrual.PRORATED_DAILY && quantity > 0
        ? trimDecimal(totalAmount / quantity, 6)
        : unitCost;
    const itemTax = totalAmount * (service.taxRate?.taxRate || 0) * 0.01;
    const itemTotal = totalAmount + itemTax;
    const tier =
      tierIndex >= 0
        ? {
            tierIndex,
            days: trimDecimal(totalDays || 0),
            unitCost,
          }
        : null;

    items.push({
      serviceId: service.id,
      serviceDate,
      event: ChargeEvent.STORAGE,
      quantity,
      units: rule.billingUOM,
      unitCost: lineUnitCost,
      amount: trimDecimal(totalAmount, 2),
      taxRate: service.taxRate,
      itemTax: trimDecimal(itemTax, 2),
      itemTotal: trimDecimal(itemTotal, 2),
      serviceNote: unitDays
        ? getUnitDayServiceNote({
            rule,
            units,
            unitDays,
            dailyRate: unitCost,
            tier,
            label: serviceNote,
          })
        : serviceNote ||
          getStorageTierServiceNote(rule, quantity, lineUnitCost, null, tier),
    });
  }

  return items;
};

export const calcSubtotal = (lineItems) => {
  if (lineItems?.items)
    return lineItems.items
      .map((item) => (item ? Number(item.amount) || 0 : 0))
      .reduce((accum, curr) => accum + curr, 0);
  return 0.0;
};

export const calcTaxTotal = (lineItems) => {
  if (lineItems?.items)
    return lineItems.items
      .map((item) => (item ? Number(item.itemTax) || 0 : 0))
      .reduce((accum, curr) => accum + curr, 0);
  return 0.0;
};

// First generated invoice with more than MAX_INVOICE_LINE_ITEMS line items,
// or null. Checked before /invoiceCreateBulk so nothing is written.
export function findOversizedInvoice(invoiceCreateList, invoiceServiceCreateMap) {
  for (const invoice of invoiceCreateList || []) {
    const lineCount = invoiceServiceCreateMap?.[invoice.id]?.length ?? 0;
    if (lineCount > MAX_INVOICE_LINE_ITEMS) return { invoice, lineCount };
  }
  return null;
}

export function computeAggregatedShipmentFields(shipments = []) {
  const poNumbers = new Set();
  const railCarNumbers = new Set();
  const pickupNumbers = new Set();

  shipments.forEach((shipment) => {
    if (shipment.poNumber) poNumbers.add(shipment.poNumber);
    if (shipment.railCarNumber) railCarNumbers.add(shipment.railCarNumber);
    if (shipment.pickupNumber) pickupNumbers.add(shipment.pickupNumber);
  });

  return {
    poNumbers: [...poNumbers],
    railCarNumbers: [...railCarNumbers],
    pickupNumbers: [...pickupNumbers],
  };
}

export const createTransform = (data, prefix, counter, isSyncEnabled) => {
  debugger;
  data.active = "true";
  data.groups = window.appUser?.groups;
  data.reloadYardId = window.appUser?.reloadYardId;
  data.accountName = data.accountName
    ? data.accountName
    : data.account?.businessName;
  data.status = InvoiceStatus.NEW;
  data.dueDate = endOfDay(data.dueDate).toISOString();
  data.invoiceNumber = `${prefix}-${counter}`;

  data.poNumbers =
    data.poNumbers ??
    [
      data.poNumber ||
        data.inboundShipment?.poNumber ||
        data.outboundShipment?.poNumber,
    ].filter(Boolean);

  data.railCarNumbers =
    data.railCarNumbers ??
    [
      data.inboundShipment?.railCarNumber ||
        data.outboundShipment?.railCarNumber,
    ].filter(Boolean);

  if (!data.pickupNumbers) {
    const pickupNo = data.outboundShipment?.pickupNumber;
    data.pickupNumbers = pickupNo ? [pickupNo] : [];
  }

  const subtotal = calcSubtotal(data.lineItems);
  data.subtotal = subtotal ? Number(subtotal).toFixed(2) : 0.0;
  data.adjustment = 0.0;
  data.discount = 0.0;
  const taxTotal = calcTaxTotal(data.lineItems);
  data.tax = taxTotal ? Number(taxTotal).toFixed(2) : 0.0;
  data.total = Number(Number(data.subtotal) + Number(data.tax)).toFixed(2);
  data.balance = data.total ? data.total : 0.0;

  data.contact = !data.contact ? null : data.contact;

  if (isSyncEnabled) {
    data.externalStatus = ExternalStatus.SYNC_PENDING;
  }

  // createdAt / updatedAt needed for lambda and ignored on create
  const sanitized = sanitizeInvoiceLambda(data);

  // clear up input shipment fields
  delete sanitized.areaId;
  delete sanitized.commodity;
  delete sanitized.tareWeight;
  delete sanitized.ladenWeight;
  delete sanitized.weightUnits;

  return sanitized;
};

export const createInvoiceLambda = (data) => {
  debugger;
  data.id = uuidv4();
  data.__typename = "Invoice";
  data.owner = window.appUser?.owner;
  data.createdAt = new Date().toISOString();
  data.updatedAt = data.createdAt;

  return data;
};

export const createLambdaInvoiceServices = (data) => {
  const lineItems = data.lineItems?.items ?? [];

  let invoiceServiceList = lineItems.map((item, idx) => {
    const input = createInvoiceServiceLambda(data, item, idx);
    return input;
  });

  return invoiceServiceList;
};

export const createInvoiceServiceLambda = (baseInvoice, lineItem, index) => {
  debugger;
  const amount = Number(lineItem.amount ?? 0);
  const itemTax = Number(lineItem.itemTax ?? 0);
  const itemTotal = amount + itemTax;

  const input = {
    id: uuidv4(),
    __typename: "InvoiceService",
    owner: baseInvoice.owner,
    groups: baseInvoice.groups,
    createdAt: baseInvoice.createdAt,
    updatedAt: baseInvoice.createdAt,
    invoiceId: baseInvoice.id,
    accountId: baseInvoice.accountId,
    reloadYardId: baseInvoice.reloadYardId,
    serviceId: lineItem.serviceId,
    serviceNote: lineItem.serviceNote,
    quantity: lineItem.quantity,
    units: lineItem.units,
    unitCost: lineItem.unitCost,
    amount: trimDecimal(amount, 2),
    taxRate: lineItem.taxRate,
    itemTax: trimDecimal(itemTax, 2),
    itemTotal: trimDecimal(itemTotal, 2),
    serviceDate: lineItem.serviceDate,
    sortKey: index + SORTKEY_BUFFER,
  };

  // Resolve shipment linkage first.
  // NOTE: event resolution is handled AFTER this so we can infer from linkage,
  // while still allowing explicit lineItem.event (e.g. STORAGE) to win.
  let inferredEvent = null;
  if (lineItem.inboundShipmentId || baseInvoice.inboundShipmentId) {
    input.inboundShipmentId =
      lineItem.inboundShipmentId || baseInvoice.inboundShipmentId;
    inferredEvent = ChargeEvent.INBOUND;
  }
  if (lineItem.outboundShipmentId || baseInvoice.outboundShipmentId) {
    input.outboundShipmentId =
      lineItem.outboundShipmentId || baseInvoice.outboundShipmentId;
    inferredEvent = ChargeEvent.OUTBOUND;
  }

  // Event precedence:
  // 1) Explicit line item event (e.g. STORAGE) if provided
  // 2) Parent invoice event
  // 3) Infer from shipment linkage
  const resolvedEvent = lineItem.event ?? baseInvoice.event ?? inferredEvent;
  if (resolvedEvent) input.event = resolvedEvent;

  return input;
};

export const updateLambdaInbound = (inbound, invoiceData) => {
  const historyNote = {
    text: `Invoice created`,
    by: window.appUser?.fullName,
    date: invoiceData.createdAt,
    type: NoteType.GENERIC,
  };

  debugger;
  const inboundUpdateInput = {
    id: inbound.id,
    updatedAt: invoiceData.createdAt,
    invoiceId: invoiceData.id,
    history: [...inbound.history, historyNote],
  };

  delete inboundUpdateInput.notes;
  delete inboundUpdateInput.sealNumbers;
  const sanitized = sanitizeInboundLambda(inboundUpdateInput);
  return sanitized;
};

export const updateLambdaOutbound = (outbound, invoiceData) => {
  const historyNote = {
    text: `Invoice created`,
    by: window.appUser?.fullName,
    date: invoiceData.createdAt,
    type: NoteType.GENERIC,
  };

  debugger;
  const outboundUpdateInput = {
    id: outbound.id,
    updatedAt: invoiceData.createdAt,
    invoiceId: invoiceData.id,
    history: [...outbound.history, historyNote],
  };

  delete outboundUpdateInput.notes;
  const sanitized = sanitizeOutboundLambda(outboundUpdateInput);
  return sanitized;
};

export const updateLambdaTransform = (invoices) => {
  const exportDate = new Date().toISOString();
  let invoiceUpdateList = [];
  debugger;

  if (invoices) {
    invoices.forEach((item) => {
      if (item.status === InvoiceStatus.NEW) {
        const invoiceUpdateInput = {
          ...item,
          updatedAt: exportDate,
          status: InvoiceStatus.PENDING,
        };

        const sanitized = sanitizeInvoiceLambda(invoiceUpdateInput);
        invoiceUpdateList.push(sanitized);
      }
    });
  }

  return invoiceUpdateList;
};

export const getMemo = (invoice) => {
  let memo = "";
  // TODO: remove - using arrays of numbers, and in service note
  // if (invoice.poNumber) memo = memo + `PO Number: ${invoice.poNumber}\n`;
  // if (invoice.railCarNumber)
  //   memo = memo + `Car/Trl Number: ${invoice.railCarNumber}\n`;
  // if (invoice.pickupNumber)
  //   memo = memo + `Pickup Number Number: ${invoice.pickupNumber}\n`;
  if (invoice.notes) memo = memo + invoice.notes;

  return memo;
};

export const getDueDate = (paymentTerm) => {
  let terms = null;
  if (typeof paymentTerm === "object" && paymentTerm !== null) {
    terms = paymentTerm;
  } else {
    terms = getPaymentTerms(paymentTerm);
  }
  const dueDays = terms?.dueDays ?? 0;
  const dueDate = addDays(new Date(), dueDays);
  return dueDate;
};

export const getPaymentTerms = (paymentTermId, termList) => {
  const allTerms =
    termList || window.appUser?.reloadYard?.paymentTerms?.items || [];

  const terms = paymentTermId
    ? allTerms.find((i) => i.id === paymentTermId)
    : null;
  return terms;
};

// exporter helper functions
export const getPaymentTermsName = (paymentTermId) => {
  const terms = getPaymentTerms(paymentTermId);
  return terms?.name ?? "";
};

export const getServiceDate = (invoice, lineItem) => {
  return new Date(
    lineItem.serviceDate || invoice.createdAt,
  ).toLocaleDateString();
};

/**
 * Generate service note for line item
 * @param {Object} rule - Charge rule object
 * @param {number} quantity - Calculated quantity
 * @param {number} unitPrice - Unit price
 * @param {Object} shipment - Record data matching the event type
 * @returns {string} Service note
 */
export const getServiceNote = (rule, quantity, unitPrice, shipment) => {
  const parts = [];

  // Add service name
  parts.push(`${rule.service?.description || rule.service?.name || ""}`);

  // Add quantity and units
  if (!shipment && quantity && rule.billingUOM) {
    parts.push(`${quantity} ${rule.billingUOM}`);
  }

  // Add rate information
  if (!shipment && unitPrice && rule.billingUOM) {
    parts.push(`@ $${unitPrice}/${rule.billingUOM}`);
  }

  // Add record-specific information
  parts.push(...getShipmentNoteParts(shipment));

  return parts.join(" - ");
};

const getShipmentNoteParts = (shipment) => {
  const parts = [];

  if (shipment?.poNumber) {
    parts.push(`PO #: ${shipment.poNumber}`);
  }

  if (shipment?.pickupNumber) {
    parts.push(`Pickup #: ${shipment.pickupNumber}`);
  }

  if (shipment?.railCarNumber) {
    parts.push(`Car/Trl #: ${shipment.railCarNumber}`);
  }

  return parts;
};

export const getPoNumber = (invoice, lineItem) => {
  const poNumber =
    lineItem.inboundShipment?.poNumber ||
    lineItem.outboundShipment?.poNumber ||
    invoice.poNumber;
  return poNumber || "";
};

export const isInvoiced = (shipment) => {
  return (
    // uninvoiced.invoiceId = " ", so not invoice object
    (shipment?.invoiceId?.trim() !== "" && shipment.invoice) ||
    (shipment.invoicedServices?.items?.length ?? 0) > 0
  );
};

export function getTotalInvoiced(shipment) {
  if (!shipment?.invoicedServices?.items?.length) return 0;

  const total = shipment.invoicedServices.items.reduce((sum, item) => {
    const val = Number(item?.itemTotal ?? 0);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);

  return total;
}

export const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "");

export const getUnitChoices = (scopedFormData, services) => {
  let units = [];
  if (scopedFormData && services) {
    const service = services.find((itm) => itm.id === scopedFormData.serviceId);
    if (service) {
      units = UnitOptions.map((unit) => {
        const column = UnitToColumn[unit.id];
        const cost = Number(service[column]).toFixed(2);
        return { ...unit, name: `${unit.name} : $${cost}` };
      });
    }
  }

  return units;
};

export const logInvoiceCreateHistory = (invoice) => {
  const historyNote = {
    parentId: invoice.id,
    parentType: ResourceType.INVOICE,
    text: `Invoice created : total due: ${invoice.total}`,
  };
  logHistory(historyNote);
};

export const fetchAccountAndServices = async (accountId) => {
  if (!accountId) throw new Error("Missing accountId");

  const acctPromise = API.graphql(
    graphqlOperation(getAccount, { id: accountId }),
  );

  const servicesPromise = fetchServices(accountId, true, true);

  const [acctResp, servicesResp] = await Promise.all([
    acctPromise,
    servicesPromise,
  ]);

  const account = acctResp.data.getAccount;
  const services = servicesResp;

  return {
    account,
    services,
  };
};

export const setInvoiceSyncPending = async (invoice) => {
  try {
    const invoiceInput = {
      id: invoice.id,
      externalStatus: ExternalStatus.SYNC_PENDING,
    };
    await performUpdateInvoiceMini(invoiceInput);
  } catch (err) {}
};

export const setInvoiceSyncFailed = async (invoice) => {
  try {
    const invoieInput = {
      id: invoice.id,
      externalStatus: ExternalStatus.SYNC_FAILED,
    };
    await performUpdateInvoiceMini(invoieInput);
  } catch (err) {}
};
